import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { storeService } from '../../services/storeService';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { FormField } from '../../components/ui/FormField';
import { formatPhoneMask } from '../../utils/phone';
import { GuestInfo } from './types';

type LookupState = 'idle' | 'searching' | 'found' | 'not-found';

const DISABLED_FIELD_CLASS =
  'disabled:border-champagne-dark/40 disabled:text-text-light disabled:bg-champagne/20 disabled:cursor-not-allowed disabled:select-none';

const READONLY_FIELD_CLASS =
  'read-only:border-champagne-dark/50 read-only:text-text-medium read-only:bg-champagne/40 read-only:cursor-not-allowed';

interface GuestInfoStepProps {
  guestInfo: GuestInfo;
  onChange: (info: GuestInfo) => void;
  onNext: () => void;
}

export function GuestInfoStep({ guestInfo, onChange, onNext }: GuestInfoStepProps) {
  const [errors, setErrors] = useState<Partial<Record<keyof GuestInfo, string>>>({});
  const [showOptional, setShowOptional] = useState(false);
  const [lookupState, setLookupState] = useState<LookupState>('idle');
  const [lookupResult, setLookupResult] = useState<{ maskedName: string; city: string } | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [cep, setCep] = useState('');
  const [cepLoading, setCepLoading] = useState(false);

  useEffect(() => {
    const digits = guestInfo.phone.replace(/\D/g, '');

    if (digits.length < 10) {
      setLookupState('idle');
      setLookupResult(null);
      setConfirmed(false);
      return;
    }

    setLookupState('searching');
    const timer = setTimeout(async () => {
      try {
        const result = await storeService.lookupCustomer(digits);
        setLookupResult(result);
        setLookupState('found');
      } catch {
        setLookupState('not-found');
        setLookupResult(null);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [guestInfo.phone]);

  const handleConfirm = () => {
    setConfirmed(true);
    onChange({ ...guestInfo, name: '', neighborhood: '', city: lookupResult?.city ?? '' });
  };

  const handleDeny = () => {
    setConfirmed(false);
    setLookupState('not-found');
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmed(false);
    onChange({ ...guestInfo, phone: formatPhoneMask(e.target.value) });
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    const masked = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    setCep(masked);
  };

  const handleCepSearch = async () => {
    const digits = cep.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const address = await storeService.lookupCep(digits);
      onChange({
        ...guestInfo,
        neighborhood: address.neighborhood ?? guestInfo.neighborhood,
        city: address.city ?? guestInfo.city,
      });
    } catch {
    } finally {
      setCepLoading(false);
    }
  };

  const handleSubmit = () => {
    const newErrors: Partial<Record<keyof GuestInfo, string>> = {};

    if (!guestInfo.phone.trim() || guestInfo.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'Celular é obrigatório';
    }

    if (!confirmed) {
      if (!guestInfo.name.trim()) newErrors.name = 'Nome é obrigatório';
      if (!guestInfo.neighborhood.trim()) newErrors.neighborhood = 'Bairro é obrigatório';
      if (!guestInfo.city.trim()) newErrors.city = 'Cidade é obrigatória';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onNext();
  };

  const fieldsEnabled = lookupState === 'not-found';

  return (
    <div>
      <div className="flex items-start gap-2.5 sm:gap-3 bg-champagne/60 rounded-xl p-3 sm:p-3.5 mb-4 sm:mb-5 border border-champagne-dark/30">
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-chocolate/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg className="w-4 h-4 text-chocolate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <div>
          <p className="text-sm text-text-dark">
            Já tem conta?{' '}
            <Link to="/login" className="text-chocolate font-semibold underline hover:text-chocolate-dark">
              Faça login
            </Link>{' '}
            para acompanhar seus pedidos.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5 mb-4">
        <h2 className="text-base font-semibold text-text-dark mb-4">Seus dados</h2>

        <div className="flex flex-col gap-4">
          <FormField label="Celular" required>
            <div className="relative">
              <Input
                type="tel"
                value={guestInfo.phone}
                onChange={handlePhoneChange}
                placeholder="(00) 00000-0000"
                inputMode="numeric"
                readOnly={confirmed}
                error={errors.phone}
                className={`pr-9 ${READONLY_FIELD_CLASS}`}
              />
              {lookupState === 'searching' && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Spinner size="sm" />
                </div>
              )}
              {((lookupState === 'found' && !confirmed) || confirmed) && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <svg className="w-4 h-4 text-olivine" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
            {lookupState === 'idle' && guestInfo.phone.replace(/\D/g, '').length < 10 && (
              <p className="text-xs text-text-light mt-1">Preencha o celular para liberar os demais campos</p>
            )}
          </FormField>

          {lookupState === 'found' && !confirmed && lookupResult && (
            <div className="bg-champagne/60 border border-champagne-dark/30 rounded-xl p-4">
              <div className="flex items-start gap-2.5 mb-3">
                <div className="w-7 h-7 rounded-full bg-chocolate/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-chocolate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-text-dark">Cadastro encontrado!</p>
                  <p className="text-sm text-text-medium mt-0.5">
                    Encontramos um cadastro para <span className="font-medium text-text-dark">{lookupResult.maskedName}</span> em{' '}
                    <span className="font-medium text-text-dark">{lookupResult.city}</span>. Este é você?
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="flex-1 bg-chocolate text-white text-sm font-semibold rounded-lg px-4 py-2.5 hover:bg-chocolate-dark transition-colors"
                >
                  Sim, sou eu
                </button>
                <button
                  type="button"
                  onClick={handleDeny}
                  className="flex-1 border border-champagne-dark text-text-medium text-sm font-medium rounded-lg px-4 py-2.5 hover:border-chocolate hover:text-chocolate hover:bg-champagne/40 transition-colors"
                >
                  Não, continuar
                </button>
              </div>
            </div>
          )}

          {confirmed && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-2.5">
              <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-blue-700">Seus dados cadastrados serão utilizados.</p>
                <p className="text-xs text-blue-600 mt-0.5">
                  Para alterar suas informações, crie uma conta ou solicite a vendedora.
                </p>
                <Link
                  to="/login"
                  className="inline-block text-xs font-medium text-blue-700 underline mt-1.5 hover:text-blue-900"
                >
                  Já tem conta? Faça login
                </Link>
              </div>
            </div>
          )}

          {!confirmed && (
            <>
              <FormField label="Nome" required>
                <Input
                  type="text"
                  value={guestInfo.name}
                  onChange={(e) => onChange({ ...guestInfo, name: e.target.value })}
                  placeholder="Seu nome completo"
                  disabled={!fieldsEnabled}
                  error={errors.name}
                  className={DISABLED_FIELD_CLASS}
                />
              </FormField>

              <FormField label="CEP">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={cep}
                    onChange={handleCepChange}
                    placeholder="00000-000"
                    disabled={!fieldsEnabled}
                    className={`flex-1 ${DISABLED_FIELD_CLASS}`}
                  />
                  <button
                    type="button"
                    onClick={handleCepSearch}
                    disabled={!fieldsEnabled || cepLoading || cep.replace(/\D/g, '').length !== 8}
                    className="px-4 py-2.5 text-sm font-medium text-white bg-chocolate hover:bg-chocolate-dark rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    {cepLoading ? '...' : 'Buscar'}
                  </button>
                </div>
                <p className="text-xs text-text-light mt-1">Preenche bairro e cidade automaticamente</p>
              </FormField>

              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <FormField label="Bairro" required>
                  <Input
                    type="text"
                    value={guestInfo.neighborhood}
                    onChange={(e) => onChange({ ...guestInfo, neighborhood: e.target.value })}
                    placeholder="Seu bairro"
                    disabled={!fieldsEnabled}
                    error={errors.neighborhood}
                    className={DISABLED_FIELD_CLASS}
                  />
                </FormField>

                <FormField label="Cidade" required>
                  <Input
                    type="text"
                    value={guestInfo.city}
                    onChange={(e) => onChange({ ...guestInfo, city: e.target.value })}
                    placeholder="Sua cidade"
                    disabled={!fieldsEnabled}
                    error={errors.city}
                    className={DISABLED_FIELD_CLASS}
                  />
                </FormField>
              </div>
            </>
          )}
        </div>
      </div>

      {!confirmed && (
        <div className={['bg-white rounded-xl shadow-sm overflow-hidden mb-4', !fieldsEnabled ? 'opacity-40 pointer-events-none' : ''].join(' ')}>
          <button
            type="button"
            onClick={() => setShowOptional(!showOptional)}
            disabled={!fieldsEnabled}
            className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-champagne/30 transition-colors disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-2.5">
              <svg className="w-4 h-4 text-chocolate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-sm font-medium text-text-dark">Dados adicionais</span>
              <span className="text-xs text-text-light">(opcional)</span>
            </div>
            <svg
              className={`w-4 h-4 text-text-light transition-transform duration-200 ${showOptional ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showOptional && (
            <div className="px-5 pb-5 pt-1 border-t border-champagne-dark/20">
              <p className="text-xs text-text-light mb-4">
                Preencha para uma experiência mais personalizada. Se preferir, a vendedora pode completar depois.
              </p>

              <div className="flex flex-col gap-4">
                <FormField label="E-mail">
                  <Input
                    type="email"
                    value={guestInfo.email}
                    onChange={(e) => onChange({ ...guestInfo, email: e.target.value })}
                    placeholder="seu@email.com"
                  />
                </FormField>

                <FormField label="CPF/CNPJ">
                  <Input
                    type="text"
                    value={guestInfo.cpfCnpj}
                    onChange={(e) => onChange({ ...guestInfo, cpfCnpj: e.target.value })}
                    placeholder="000.000.000-00"
                  />
                </FormField>

                <FormField label="Nome(s) do(s) filho(s)">
                  <Input
                    type="text"
                    value={guestInfo.babyName}
                    onChange={(e) => onChange({ ...guestInfo, babyName: e.target.value })}
                    placeholder="Se mais de um, separe por vírgula"
                  />
                  <p className="text-xs text-text-light mt-1">Ex: Maria, João</p>
                </FormField>

                <FormField label="Data de nascimento do filho">
                  <Input
                    type="date"
                    value={guestInfo.babyBirthdate}
                    onChange={(e) => onChange({ ...guestInfo, babyBirthdate: e.target.value })}
                  />
                </FormField>
              </div>
            </div>
          )}
        </div>
      )}

      <Button onClick={handleSubmit} className="w-full" disabled={!confirmed && (lookupState === 'searching' || lookupState === 'found')}>
        {lookupState === 'searching' ? 'Verificando...' : 'Continuar'}
      </Button>
    </div>
  );
}
